import { Moment } from 'moment-timezone'
import * as React from 'react'

import {
  buildInitialDesiredWeeklyScheduleParams,
  IDesiredWeeklyScheduleParams,
} from 'main/components/family/DesiredWeeklyScheduleFields'
import { IChild } from 'main/gql/child'
import { UnreachableCaseError } from 'main/util/ts'
import { ITrackData, track } from '../track'
import {
  ScheduleTourModal_desiredWeeklySchedule,
  ScheduleTourModal_school,
  ScheduleTourModal_tourOpenings,
  ScheduleTourModal_upcomingTours,
  ScheduleTourModal_user,
} from './common/gql'
import Cancel from './steps/Cancel'
import CancelConfirmation from './steps/CancelConfirmation'
import Confirmation from './steps/Confirmation'
import Contact from './steps/Contact'
import Details from './steps/Details'
import OpeningSelection from './steps/OpeningSelection'
import NotTouring from './steps/OpeningSelection/NotTouring'
import Review from './steps/Review'
import { AuthedStep } from './types'

interface IProps {
  trackData: ITrackData
  data: {
    desiredWeeklySchedule: ScheduleTourModal_desiredWeeklySchedule | null
    school: ScheduleTourModal_school
    upcomingTours: ScheduleTourModal_upcomingTours[]
    tourOpenings: ScheduleTourModal_tourOpenings[]
    user: ScheduleTourModal_user
  }
  anchorMoment: Moment
  initialTourOpeningId?: string
  step: AuthedStep
  onUpdate?: () => void
  onChangeStep: (step: AuthedStep) => void
  onRequestClose: () => void
}

export default function Content(props: IProps) {
  const {
    trackData,
    anchorMoment,
    initialTourOpeningId,
    data: { desiredWeeklySchedule, school, tourOpenings, upcomingTours, user },
    step,
    onUpdate,
    onChangeStep,
    onRequestClose,
  } = props

  if (!school) throw new Error('No school')

  const [tour, setTour] = React.useState<ScheduleTourModal_upcomingTours | null>(
    upcomingTours ? upcomingTours[0] : null,
  )
  const [selectedChildren, setSelectedChildren] = React.useState<IChild[]>(
    initialSelectedChildren(user, tour),
  )

  const [selectedOpening, setSelectedOpening] =
    React.useState<ScheduleTourModal_tourOpenings | null>(
      tourOpenings.find(tourOpening => tourOpening.id === initialTourOpeningId) || null,
    )

  const [desiredWeeklyScheduleParams, setDesiredWeeklyScheduleParams] =
    React.useState<IDesiredWeeklyScheduleParams>(
      buildInitialDesiredWeeklyScheduleParams(desiredWeeklySchedule),
    )

  switch (step.__stepname) {
    case 'not_touring':
      return (
        <NotTouring
          school={school}
          onComplete={() => onChangeStep({ __stepname: 'contact', contactSchoolStep: null })}
          onRequestClose={onRequestClose}
        />
      )
    case 'contact':
      return (
        <Contact school={school} onChangeStep={onChangeStep} onComplete={() => onRequestClose()} />
      )
    case 'opening_selection':
      return (
        <OpeningSelection
          openings={tourOpenings}
          selectedOpening={selectedOpening}
          school={school}
          onSelectOpening={setSelectedOpening}
          onComplete={() => {
            track('completed_opening_selection', trackData)
            onChangeStep({ __stepname: 'details' })
          }}
        />
      )
    case 'details':
      if (!selectedOpening) throw new Error('No selected opening')
      if (!user) throw new Error('No user')
      return (
        <Details
          children={user.family.children}
          school={school}
          tour={tour}
          desiredWeeklyScheduleParams={desiredWeeklyScheduleParams}
          initialSelectedChildren={selectedChildren}
          selectedOpening={selectedOpening}
          onChangeDesiredWeeklyScheduleParams={setDesiredWeeklyScheduleParams}
          onComplete={newSelectedChildren => {
            track('completed_details', trackData)
            setSelectedChildren(newSelectedChildren)
            onChangeStep({ __stepname: 'review' })
          }}
          onRequestOpeningSelection={() => onChangeStep({ __stepname: 'opening_selection' })}
        />
      )
    case 'review':
      if (!selectedOpening) throw new Error('No selected opening')
      return (
        <Review
          anchorMoment={anchorMoment}
          desiredWeeklyScheduleParams={desiredWeeklyScheduleParams}
          school={school}
          selectedChildren={selectedChildren}
          selectedOpening={selectedOpening}
          tour={tour}
          user={user}
          onComplete={scheduledTour => {
            track('completed_review', trackData)
            setTour(scheduledTour)
            onChangeStep({ __stepname: 'confirmation_active' })

            if (onUpdate) {
              onUpdate()
            }
          }}
          onRequestDetails={() => onChangeStep({ __stepname: 'details' })}
          onRequestOpeningSelection={() => onChangeStep({ __stepname: 'opening_selection' })}
        />
      )
    case 'confirmation':
    case 'confirmation_active':
      if (!tour) throw new Error('No tour')
      return (
        <Confirmation
          active={step.__stepname === 'confirmation_active'}
          school={school}
          tour={tour}
          user={user}
          onRequestCancel={() => onChangeStep({ __stepname: 'cancel' })}
          onRequestReschedule={() => onChangeStep({ __stepname: 'opening_selection' })}
          onComplete={() => {
            track('completed_confirmation', trackData)
            onRequestClose()
          }}
        />
      )
    case 'cancel':
      if (!tour) throw new Error('No tour')
      return (
        <Cancel
          anchorMoment={anchorMoment}
          school={school}
          tour={tour}
          onAbort={() => onChangeStep({ __stepname: 'confirmation' })}
          onComplete={() => {
            track('completed_cancel', trackData)
            onChangeStep({ __stepname: 'cancel_confirmation' })

            if (onUpdate) {
              onUpdate()
            }
          }}
        />
      )
    case 'cancel_confirmation':
      return (
        <CancelConfirmation
          school={school}
          onComplete={() => {
            track('completed_cancel_confirmation', trackData)
            onRequestClose()
          }}
        />
      )
    default:
      throw new UnreachableCaseError(step)
  }
}

function initialSelectedChildren(
  user: ScheduleTourModal_user,
  tour: ScheduleTourModal_upcomingTours | null,
) {
  const {
    family: { children },
  } = user

  if (tour) {
    const tourChildIds = tour ? tour.children.map(c => c.id) : []

    return children.filter(c => tourChildIds.includes(c.id))
  } else {
    return children.length === 1 ? children : []
  }
}
